import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Easter egg: type the Konami code (↑ ↑ ↓ ↓ ← → ← → B A) to fire a confetti
 * burst + a little toast. Confetti is a self-contained canvas animation, so it
 * adds no dependencies. Also exposed via a tiny "psst" hint in the footer.
 */
const Canvas = styled.canvas`
  position: fixed;
  inset: 0;
  z-index: 9995;
  pointer-events: none;
`;

const Toast = styled(motion.div)`
  position: fixed;
  left: 50%;
  bottom: 2rem;
  transform: translateX(-50%);
  z-index: 9996;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.2rem;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--elev-bg);
  backdrop-filter: blur(12px);
  box-shadow: var(--card-shadow);
  color: var(--text);
  font-weight: 600;
  font-size: 0.92rem;
  .emoji { font-size: 1.1rem; }
`;

const SEQUENCE = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a',
];

const COLORS = ['#6366f1', '#818cf8', '#a855f7', '#c084fc', '#ec4899', '#f472b6', '#22d3ee'];

const KonamiEgg = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const posRef = useRef(0);
  const [show, setShow] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const parts = particlesRef.current;
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const p = parts[i];
      p.vy += 0.14; // gravity
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= 1;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life / 60));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      ctx.restore();

      if (p.life <= 0 || p.y > canvas.height + 40) parts.splice(i, 1);
    }

    if (parts.length > 0) {
      rafRef.current = requestAnimationFrame(loop);
    } else {
      stop();
    }
  }, [stop]);

  const fire = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const bursts = [
      { x: window.innerWidth * 0.5, y: window.innerHeight * 0.35 },
      { x: window.innerWidth * 0.15, y: window.innerHeight * 0.5 },
      { x: window.innerWidth * 0.85, y: window.innerHeight * 0.5 },
    ];
    bursts.forEach(({ x, y }) => {
      for (let i = 0; i < 70; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 9;
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 4,
          size: 6 + Math.random() * 8,
          color: COLORS[(Math.random() * COLORS.length) | 0],
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          life: 90 + Math.random() * 60,
        });
      }
    });

    setShow(true);
    window.clearTimeout(fire._t);
    fire._t = window.setTimeout(() => setShow(false), 3200);

    if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  useEffect(() => {
    const onKey = (e) => {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const expected = SEQUENCE[posRef.current];
      if (key === expected) {
        posRef.current += 1;
        if (posRef.current === SEQUENCE.length) {
          posRef.current = 0;
          fire();
        }
      } else {
        posRef.current = key === SEQUENCE[0] ? 1 : 0;
      }
    };
    window.addEventListener('keydown', onKey);
    // let other components trigger it (e.g. footer hint)
    window.addEventListener('konami:fire', fire);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('konami:fire', fire);
      stop();
    };
  }, [fire, stop]);

  return (
    <>
      <Canvas ref={canvasRef} aria-hidden="true" />
      <AnimatePresence>
        {show && (
          <Toast
            initial={{ opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24 }}
          >
            <span className="emoji">🎉</span> You found the secret. Thanks for exploring!
          </Toast>
        )}
      </AnimatePresence>
    </>
  );
};

export default KonamiEgg;
